import axios from 'axios';

// Stage 5H frontend axios client.
//
// Token strategy (see Stage 5H decisions):
//   accessToken  : module-level memory variable; lost on hard reload.
//   refreshToken : sessionStorage; cleared on tab close.
//   password     : never stored.
//
// 401 handling: a single in-flight refresh deduplicates concurrent
// failures. On refresh failure, both tokens clear and the registered
// onAuthFailure callback runs (typically pushes the user to /login).

const REFRESH_KEY = 'glassi.refreshToken';
const REFRESH_PATH = '/api/auth/refresh';

let _accessToken = null;
let _refreshInFlight = null;
let _onAuthFailure = null;

function getRefreshToken() {
  try {
    return window.sessionStorage.getItem(REFRESH_KEY);
  } catch (_e) {
    return null;
  }
}

function setRefreshToken(token) {
  try {
    if (token) window.sessionStorage.setItem(REFRESH_KEY, token);
    else window.sessionStorage.removeItem(REFRESH_KEY);
  } catch (_e) {
    /* noop */
  }
}

export function getAccessToken() {
  return _accessToken;
}

export function setTokens({ accessToken, refreshToken }) {
  if (accessToken !== undefined) _accessToken = accessToken || null;
  if (refreshToken !== undefined) setRefreshToken(refreshToken || null);
}

export function clearTokens() {
  _accessToken = null;
  setRefreshToken(null);
}

export function hasStoredRefreshToken() {
  return Boolean(getRefreshToken());
}

export function registerAuthFailureHandler(fn) {
  _onAuthFailure = typeof fn === 'function' ? fn : null;
}

const apiClient = axios.create({
  baseURL: '/',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' }
});

apiClient.interceptors.request.use((config) => {
  if (_accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_accessToken}`;
  }
  return config;
});

async function performRefresh() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('no refresh token');

  // Use a bare axios call to bypass interceptors and avoid recursion.
  const resp = await axios.post(REFRESH_PATH, { refreshToken });
  const data = resp.data && resp.data.data;
  if (!data || !data.accessToken || !data.refreshToken) {
    throw new Error('refresh response missing tokens');
  }
  setTokens({
    accessToken: data.accessToken,
    refreshToken: data.refreshToken
  });
  return data.accessToken;
}

apiClient.interceptors.response.use(
  (resp) => resp,
  async (err) => {
    const config = err && err.config;
    const status = err && err.response && err.response.status;

    if (status !== 401 || !config) return Promise.reject(err);

    // Don't try to refresh the refresh endpoint or retry forever.
    if (config.url && config.url.includes(REFRESH_PATH)) {
      clearTokens();
      if (_onAuthFailure) _onAuthFailure();
      return Promise.reject(err);
    }
    if (config.__isRetry) return Promise.reject(err);

    if (!getRefreshToken()) {
      clearTokens();
      if (_onAuthFailure) _onAuthFailure();
      return Promise.reject(err);
    }

    try {
      if (!_refreshInFlight) {
        _refreshInFlight = performRefresh().finally(() => {
          _refreshInFlight = null;
        });
      }
      const newAccess = await _refreshInFlight;
      const retryConfig = { ...config, __isRetry: true };
      retryConfig.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${newAccess}`
      };
      return apiClient.request(retryConfig);
    } catch (refreshErr) {
      clearTokens();
      if (_onAuthFailure) _onAuthFailure();
      return Promise.reject(refreshErr);
    }
  }
);

export default apiClient;
